class DB {
    constructor() {
        this.db = null;
        this.dbName = 'soapy';
        this.version = 2;
        this.storeName = 'store';
        this.offlineQueueName = 'offlineQueue';
        this.ready = false;
        this.persisted = false;
    }

    async init() {
        console.log('Initializing DB');

        // Request persistent storage if available
        if (navigator.storage && navigator.storage.persist) {
            try {
                this.persisted = await navigator.storage.persist();
                console.log(`Persistent storage granted: ${this.persisted}`);
            } catch (error) {
                console.warn('Failed to request persistent storage:', error);
                this.persisted = false;
            }
        }

        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.version);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                this.db = request.result;
                this.ready = true;
                resolve();
            };

            request.onupgradeneeded = (event) => {
                const db = event.target.result;

                // Create main store if it doesn't exist
                if (!db.objectStoreNames.contains(this.storeName)) {
                    db.createObjectStore(this.storeName);
                }

                // Create offline queue store if it doesn't exist
                if (!db.objectStoreNames.contains(this.offlineQueueName)) {
                    db.createObjectStore(this.offlineQueueName, { autoIncrement: true });
                }
            };
        });
    }

    async reset() {
        return new Promise((resolve) => {
            if (this.db) {
                this.clearAll();
                this.db.close();
            }
            this.db = null;
            this.ready = false;
            this.persisted = false;
            resolve();
        });
    }

    async clearAll() {
        return new Promise((resolve) => {
            this.clearTable(this.storeName);
            this.clearTable(this.offlineQueueName);
            resolve();
        });
    }

    async clearTable(table) {
        return new Promise((resolve) => {
            const transaction = this.db.transaction(table, 'readwrite');
            const store = transaction.objectStore(table);
            store.clear();
            resolve();
        });
    }

    /*
     *  Quick access methods to the key/value store
     */

    async get(key) {
        return this.getFrom(this.storeName, key);
    }

    async getMany(keys) {
        return this.getManyFrom(this.storeName, keys);
    }

    async set(key, value) {
        return this.setFrom(this.storeName, key, value);
    }

    async remove(key) {
        return this.removeFrom(this.storeName, key);
    }

    async getAllKeys() {
        return this.getAllKeysFromFrom(this.storeName);
    }

    async getAllData() {
        return this.getAllDataFrom(this.storeName);
    }

    async clear() {
        if (!this.db) {
            return true;
        }

        await this.clearFrom(this.storeName);
        await this.clearFrom(this.offlineQueueName);
        return true;
    }

    /*
     *  Implementations which take a table name
     */

    async getFrom(table, key) {
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(table, 'readonly');
            const store = transaction.objectStore(table);
            const request = store.get(key);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result);
        });
    }

    async getManyFrom(table, keys) {
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(table, 'readonly');
            const store = transaction.objectStore(table);
            const result = {};
            let completed = 0;

            if (keys.length === 0) {
                resolve(result);
                return;
            }

            keys.forEach(key => {
                const request = store.get(key);
                request.onerror = () => reject(request.error);
                request.onsuccess = () => {
                    result[key] = request.result;
                    completed++;
                    if (completed === keys.length) {
                        resolve(result);
                    }
                };
            });
        });
    }

    async setFrom(table, key, value) {
        // console.log('setFrom', table, key, value);
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(table, 'readwrite');
            const store = transaction.objectStore(table);
            const request = store.put(value, key);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve();
        });
    }

    async removeFrom(table, key) {
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(table, 'readwrite');
            const store = transaction.objectStore(table);
            const request = store.delete(key);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve();
        });
    }

    async getAllKeysFrom(table) {
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(table, 'readonly');
            const store = transaction.objectStore(table);
            const request = store.getAllKeys();

            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result);
        });
    }

    async getAllDataFrom(table) {
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(table, 'readonly');
            const store = transaction.objectStore(table);
            const result = {};

            const request = store.openCursor();

            request.onerror = () => reject(request.error);
            request.onsuccess = (event) => {
                const cursor = event.target.result;
                if (cursor) {
                    result[cursor.key] = cursor.value;
                    cursor.continue();
                } else {
                    resolve(result);
                }
            };
        });
    }

    async clearFrom(table) {
        return new Promise((resolve, reject) => {
            const transaction = this.db.transaction(table, 'readwrite');
            const store = transaction.objectStore(table);
            const request = store.clear();

            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve();
        });
    }

    /*
     *  Wrapper methods to access a specific table
     */

    table(name) {
        return {
            get: (key) => this.getFrom(name, key),
            set: (key, value) => this.setFrom(name, key, value),
            remove: (key) => this.removeFrom(name, key),
            getAllKeys: () => this.getAllKeysFrom(name),
            getAllData: () => this.getAllDataFrom(name),
            clear: () => this.clearFrom(name),
        }
    }
}

export default new DB();
